import React from 'react';
import { useSelector } from 'react-redux';
import { useFormContext, Controller } from 'react-hook-form';
import { Checkbox, InputLabel, makeStyles, TextField } from '@material-ui/core';
import { RootState } from '@modules/index';
import { RegisterRfqInput } from '@generated/graphql';
import { numberToKorean } from '@utils/formatter';
import { colorSet } from '@components/Provider';
import { CustomizedTypography } from '@components/Customized';
import { NumberFormatCustom } from '@components/CurrencyTextfield/CurrncyTextField';
import FormInputLayout from '../Layout/FormInputLayout';
import useRequest from '@hooks/useRequest';

interface EstimateBudgetRangeInputProps {
  maxError?: string;
}

const EstimateBudgetRangeInput: React.FC<EstimateBudgetRangeInputProps> = ({
  maxError
}) => {
  const maxDefaultValue = useSelector<RootState>(
    (state) => state.request.maxEstimatedBudget
  );
  const budgetOpenDefaultValue = useSelector<RootState>(
    (state) => state.request.budgetOpen
  );

  const { control } = useFormContext<RegisterRfqInput>();
  const {
    rangeInputContainer,
    estimateBudgetRangeInput,
    budgetOpenBox
  } = useStyles();
  const { onUpdateRfqInfo } = useRequest();

  return (
    <FormInputLayout label="추정 예산" required={true}>
      <div className={rangeInputContainer}>
        <Controller
          name="maxEstimatedBudget"
          control={control}
          rules={{
            required: true,
            validate: {
              biggerThanZero: (value) =>
                Number(value ?? 0) > 0 ? true : '0보다 큰 수를 입력해주세요',
              range: (value) => {
                return value.toString().length < 13 ? true : false;
              }
            }
          }}
          defaultValue=""
          render={({ onChange, value }) => (
            <TextField
              data-cy="rfq-maxEstimatedBudget"
              defaultValue={maxDefaultValue}
              value={value ? value : ''}
              className={estimateBudgetRangeInput}
              autoComplete="off"
              variant="outlined"
              error={!!maxError}
              helperText={
                !!value && value !== 0 && numberToKorean(Number(value)) + '원'
              }
              onChange={(e) => {
                if (e.target.value?.length > 12) {
                  return;
                }
                onChange(Number(e.target.value));
              }}
              fullWidth
              placeholder="0"
              InputProps={{
                inputComponent: NumberFormatCustom,
                endAdornment: (
                  <CustomizedTypography variant="body1" color="textSecondary">
                    원
                  </CustomizedTypography>
                )
              }}
            />
          )}
        />
      </div>
      <Controller
        name="budgetOpen"
        defaultValue={budgetOpenDefaultValue ?? false}
        control={control}
        render={(renderProps) => (
          <InputLabel className={budgetOpenBox}>
            <Checkbox
              {...renderProps}
              data-cy="rfq-budgetOpen"
              size="small"
              color="primary"
              checked={!renderProps.value}
              onChange={(e) => {
                onUpdateRfqInfo({ budgetOpen: !e.target.checked });
                renderProps.onChange(!e.target.checked);
              }}
            />
            <CustomizedTypography variant="body2" color="textPrimary">
              추정예산을 파트너에게 공개하지 않습니다.
            </CustomizedTypography>
          </InputLabel>
        )}
      />
    </FormInputLayout>
  );
};

export default EstimateBudgetRangeInput;

const useStyles = makeStyles((theme) => ({
  rangeInputContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '9px',
    '& h2': {
      lineHeight: '48px',
      paddingTop: '20px'
    }
  },
  estimateBudgetRangeInput: {
    '& input': {
      padding: 12,
      textAlign: 'right'
    },
    '& .MuiInputBase-root': {
      alignItems: 'center',
      borderRadius: 8
    },
    '& ::placeholder': {
      color: colorSet.gray500
    },
    '& .MuiFormHelperText-root': {
      color: colorSet.gray700,
      textAlign: 'right',
      position: 'static',
      margin: 0
    }
  },
  budgetOpenBox: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 8,
    transform: 'translateX(-9px)',
    '&:hover': {
      cursor: 'pointer'
    }
  },
  helperText: {
    margin: '3px 14px 0px'
  }
}));
